import { useEffect, useRef, useCallback, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function useWebSocket(token, onMessage) {
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const reconnectTimer = useRef(null);
  const retryCount = useRef(0);
  const maxRetries = 3;

  const connect = useCallback(() => {
    if (!token) return;
    if (retryCount.current >= maxRetries) {
      // Silently fallback to HTTP polling mode - WebSocket not available
      return;
    }

    try {
      const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      const ws = new WebSocket(`${wsUrl}/ws/${token}`);

      ws.onopen = () => {
        setConnected(true);
        retryCount.current = 0;
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
        ws._pingInterval = pingInterval;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type !== 'pong' && onMessage) {
            onMessage(data);
          }
        } catch (e) {
          // silent
        }
      };

      ws.onclose = () => {
        setConnected(false);
        if (ws._pingInterval) clearInterval(ws._pingInterval);
        retryCount.current += 1;
        if (retryCount.current < maxRetries) {
          reconnectTimer.current = setTimeout(connect, 5000);
        }
      };

      ws.onerror = () => {
        ws.close();
      };

      wsRef.current = ws;
    } catch (e) {
      // WebSocket construction failed - silently fallback
      retryCount.current = maxRetries;
    }
  }, [token, onMessage]);

  useEffect(() => {
    connect();
    return () => {
      retryCount.current = maxRetries; // Prevent reconnect on unmount
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        if (wsRef.current._pingInterval) clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
      }
    };
  }, [connect]);

  const sendLocation = useCallback((lat, lng) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'location_update', lat, lng }));
    }
  }, []);

  return { connected, sendLocation };
}
